import { useState, useEffect, useRef } from "react";
import { MdKeyboardArrowRight } from "react-icons/md";

interface Props {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
  maxVisiblePages?: number;
  jumpPage?: number; // Number of pages to jump when clicking ellipsis
}

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className = "",
  maxVisiblePages = 5,
  jumpPage = 5
}: Props) => {
  const [visibleRange, setVisibleRange] = useState<{ start: number, end: number }>(() => {
    const halfVisible = Math.floor(maxVisiblePages / 2);
    let start: number;
    let end: number;

    if (currentPage <= halfVisible + 1) {
      start = 1;
      end = Math.min(maxVisiblePages, totalPages);
    } else if (currentPage >= totalPages - halfVisible) {
      start = Math.max(1, totalPages - maxVisiblePages + 1);
      end = totalPages;
    } else {
      start = currentPage - halfVisible;
      end = currentPage + halfVisible;
    }

    return { start, end };
  });

  const [displayCurrentPage, setDisplayCurrentPage] = useState(currentPage);
  const [hoveredEllipsis, setHoveredEllipsis] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getVisiblePageNumbers = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= maxVisiblePages + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Add ellipsis if there's a gap between 1 and visible range start
      if (visibleRange.start > 2) {
        pages.push("prev-ellipsis");
      }

      // Add visible range pages (excluding first and last if they're already included)
      for (let i = Math.max(2, visibleRange.start); i <= Math.min(totalPages - 1, visibleRange.end); i++) {
        pages.push(i);
      }

      // Add ellipsis if there's a gap between visible range end and last page
      if (visibleRange.end < totalPages - 1) {
        pages.push("next-ellipsis");
      }

      // Always show last page (if not already included)
      if (totalPages > 1 && !pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Update visible range and display current page with slower fade effect
  useEffect(() => {
    const halfVisible = Math.floor(maxVisiblePages / 2);

    // Check if we're in the middle range (where sliding should happen)
    const isInMiddleRange = currentPage > halfVisible + 1 && currentPage < totalPages - halfVisible;

    // Check if current page is first or last page (always visible)
    const isFirstOrLastPage = currentPage === 1 || currentPage === totalPages;

    if (isInMiddleRange && !isFirstOrLastPage) {
      // Clear previous timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (activeTimeoutRef.current) {
        clearTimeout(activeTimeoutRef.current);
      }

      // Step 1: Hide active immediately (fade out) - set to invalid page
      setDisplayCurrentPage(-1);

      // Step 2: Update range after longer delay for smoother transition
      timeoutRef.current = setTimeout(() => {
        const newStart = currentPage - halfVisible;
        const newEnd = currentPage + halfVisible;

        if (newStart !== visibleRange.start || newEnd !== visibleRange.end) {
          setVisibleRange({ start: newStart, end: newEnd });
        }
      }, 150); // Increased from 50ms to 150ms for slower slide

      // Step 3: Show active again after range has updated (longer fade in)
      activeTimeoutRef.current = setTimeout(() => {
        setDisplayCurrentPage(currentPage);
      }, 400); // Increased from 150ms to 400ms for slower fade in

    } else {
      // For beginning/end ranges, or first/last page clicks, update immediately
      let newStart: number;
      let newEnd: number;

      if (currentPage <= halfVisible + 1) {
        newStart = 1;
        newEnd = Math.min(maxVisiblePages, totalPages);
      } else {
        newStart = Math.max(1, totalPages - maxVisiblePages + 1);
        newEnd = totalPages;
      }

      if (newStart !== visibleRange.start || newEnd !== visibleRange.end) {
        setVisibleRange({ start: newStart, end: newEnd });
      }

      // Update display current page immediately for beginning/end or first/last page
      setDisplayCurrentPage(currentPage);
    }

    // Cleanup timeouts on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (activeTimeoutRef.current) {
        clearTimeout(activeTimeoutRef.current);
      }
    };
  }, [currentPage, totalPages, maxVisiblePages]);

  // Initialize display current page on mount
  useEffect(() => {
    setDisplayCurrentPage(currentPage);
  }, []);

  const handlePageClick = (page: number | string) => {
    if (typeof page === "number" && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handleEllipsisClick = (type: "prev" | "next") => {
    const jumpSize = jumpPage;
    let targetPage: number;

    if (type === "prev") {
      targetPage = Math.max(1, currentPage - jumpSize);
    } else {
      targetPage = Math.min(totalPages, currentPage + jumpSize);
    }

    if (targetPage !== currentPage) {
      onPageChange(targetPage);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const pageNumbers = getVisiblePageNumbers();
  if (totalPages < 1) {
    return null;
  }

  return (
    <div className={`flex items-center justify-start space-x-2 h-[3.25rem] ${className} `}>
      {/* Previous Button */}
      <button
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className={`size-9 rounded-full flex items-center justify-center transition-colors duration-300 ${currentPage === 1
          ? "cursor-not-allowed text-auro_metal_saurus"
          : "text-charleston_green hover:text-gray-800 hover:bg-gray-100"
          }`}
        aria-label="Previous page"
      >
        <MdKeyboardArrowRight className="rotate-180" />
      </button>

      {/* Page Numbers */}
      <div className="flex items-center space-x-1">
        {pageNumbers.map((page, index) => {
          if (page === "prev-ellipsis") {
            const isHovered = hoveredEllipsis === `prev-${index}`;
            return (
              <div key={`prev-ellipsis-${index}`} className="relative group">
                <button
                  onClick={() => handleEllipsisClick("prev")}
                  onMouseEnter={() => setHoveredEllipsis(`prev-${index}`)}
                  onMouseLeave={() => setHoveredEllipsis(null)}
                  className="px-3 py-2 text-charleston_green transition-all duration-300 hover:text-gray-800 hover:bg-gray-100 rounded-md cursor-pointer"
                  aria-label={`Go back ${jumpPage} pages`}
                >
                  <span className={`transition-all duration-200 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
                    ...
                  </span>
                  <span className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                    &lt;
                  </span>
                </button>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  前の {jumpPage} ページ
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            );
          }

          if (page === "next-ellipsis") {
            const isHovered = hoveredEllipsis === `next-${index}`;
            return (
              <div key={`next-ellipsis-${index}`} className="relative group">
                <button
                  onClick={() => handleEllipsisClick("next")}
                  onMouseEnter={() => setHoveredEllipsis(`next-${index}`)}
                  onMouseLeave={() => setHoveredEllipsis(null)}
                  className="px-3 py-2 text-charleston_green transition-all duration-300 hover:text-gray-800 hover:bg-gray-100 rounded-md cursor-pointer"
                  aria-label={`Go forward ${jumpPage} pages`}
                >
                  <span className={`transition-all duration-200 ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
                    ...
                  </span>
                  <span className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                    &gt;
                  </span>
                </button>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  次の {jumpPage} ページ
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                </div>
              </div>
            );
          }

          const pageNumber = page as number;
          const isActive = pageNumber === displayCurrentPage; // Use page value instead of index

          return (
            <button
              key={pageNumber}
              onClick={() => handlePageClick(pageNumber)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-500 ease-in-out ${isActive
                ? "bg-cyan text-white transform scale-105 shadow-lg"
                : "text-charleston_green hover:text-gray-800 hover:bg-gray-100 hover:scale-105"
                }`}
              aria-label={`Page ${pageNumber}`}
              aria-current={isActive ? "page" : undefined}
            >
              {pageNumber}
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <button
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className={`size-9 rounded-full flex items-center justify-center transition-colors duration-300 ${currentPage === totalPages
          ? "text-auro_metal_saurus cursor-not-allowed"
          : "text-charleston_green hover:text-gray-800 hover:bg-gray-100 hover:"
          }`}
        aria-label="Next page"
      >
        <MdKeyboardArrowRight />
      </button>
    </div>
  );
};
